import { useState, useCallback, useEffect, useRef } from 'react';
import { Loader2, Send, Paperclip, X, AlertCircle, MessageSquare } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import MessageThread from '@/routes/gestion-sav/SavV2/components/Messages/MessageThread';
import { MessageWithStatus } from '@/Api/sav-v2/savV2MessagesApi';
import { savApi } from '../api/savApi';
import type { MessageChannel } from '../hooks/useSavMessages';

interface SavChatBlockProps {
  savId: number;
  channel: MessageChannel;
  title: string;
  icon: LucideIcon;
  readOnly?: boolean;
  defaultShowInput?: boolean;
  /**
   * Nombre de messages non lus à signaler dans un bandeau en tête du fil
   * (snapshot capturé à l'ouverture du dossier, AVANT le mark-read, pour que
   * l'indication reste visible même après remise à 0 du compteur en base).
   */
  unreadHighlightCount?: number;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 Mo
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

export function SavChatBlock({ savId, channel, title, icon: Icon, readOnly = true, defaultShowInput = false, unreadHighlightCount = 0 }: SavChatBlockProps) {
  const [messages, setMessages] = useState<MessageWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Send state (only used when !readOnly)
  const [showInput, setShowInput] = useState(defaultShowInput);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const fetchMessages = channel === 'beneficiary'
    ? savApi.getBeneficiaryMessages
    : channel === 'garant'
      ? savApi.getGarantMessages
      : savApi.getInternalMessages;

  const loadMessages = useCallback(async (pageNum: number, reset: boolean) => {
    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const result = await fetchMessages(savId, pageNum);
      const filtered = result.nodes
        .filter(msg => msg.senderType !== 'system')
        .map(msg => ({ ...msg, user: msg.user ? { ...msg.user, email: undefined } : msg.user }));

      if (reset) {
        setMessages(filtered);
      } else {
        setMessages(prev => [...prev, ...filtered]);
      }
      setPage(pageNum);
      setHasMore(result.pageInfos.hasNextPage);
    } catch (error) {
      console.error('Erreur chargement messages:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [savId, fetchMessages]);

  useEffect(() => {
    loadMessages(0, true);
  }, [loadMessages]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(() => loadMessages(0, true), 300_000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  // Scroll to bottom on new messages (inside container only)
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadMessages(page + 1, false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setFileError(null);
    const invalid = files.find((f) => !ACCEPTED_TYPES.includes(f.type));
    if (invalid) {
      setFileError(`Type non supporté : ${invalid.name}. Formats acceptés : PDF, JPG, PNG.`);
      e.target.value = '';
      return;
    }
    const tooLarge = files.find((f) => f.size > MAX_FILE_SIZE);
    if (tooLarge) {
      setFileError(`Fichier trop volumineux : ${tooLarge.name} (max 10 Mo).`);
      e.target.value = '';
      return;
    }
    setPendingFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    const text = inputText.trim();
    if ((!text && pendingFiles.length === 0) || isSending) return;

    setIsSending(true);
    setInputText('');
    const filesToSend = [...pendingFiles];
    setPendingFiles([]);
    setFileError(null);
    try {
      const attachmentsIds: number[] = [];
      for (const file of filesToSend) {
        const result = await savApi.uploadDocument(savId, file, file.name);
        attachmentsIds.push(result.document.id);
      }
      await savApi.sendGarantMessage(savId, {
        text: text || (filesToSend.length > 0 ? `${filesToSend.length} fichier(s) envoyé(s)` : ''),
        ...(attachmentsIds.length > 0 ? { attachmentsIds } : {}),
      });
      await loadMessages(0, true);
    } catch {
      setInputText(text);
      setPendingFiles(filesToSend);
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-flex tw-flex-col">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-border-b tw-border-gray-200">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Icon className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
          <h3 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-m-0">{title}</h3>
          {messages.length > 0 && (
            <span className="tw-text-xs tw-font-bold tw-bg-affinicar-primary tw-text-white tw-w-6 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-justify-center">
              {messages.length}
            </span>
          )}
        </div>
      </div>

      {/* Bandeau messages non lus (snapshot à l'ouverture — reste visible après
          le mark-read côté serveur) */}
      {unreadHighlightCount > 0 ? (
        <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 sm:tw-px-4 tw-py-2 tw-bg-blue-50 tw-border-b tw-border-blue-100">
          <MessageSquare className="tw-w-4 tw-h-4 tw-text-blue-500 tw-flex-shrink-0" />
          <span className="tw-text-xs tw-font-medium tw-text-blue-700">
            {unreadHighlightCount} nouveau{unreadHighlightCount > 1 ? 'x' : ''} message
            {unreadHighlightCount > 1 ? 's' : ''} non lu{unreadHighlightCount > 1 ? 's' : ''}
          </span>
        </div>
      ) : null}

      {/* Messages */}
      <div ref={messagesContainerRef} className="tw-max-h-[250px] sm:tw-max-h-[300px] md:tw-max-h-[400px] lg:tw-max-h-[500px] tw-overflow-y-auto">
        {loading ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
            <Loader2 className="tw-w-6 tw-h-6 tw-animate-spin tw-text-affinicar-primary" />
          </div>
        ) : messages.length === 0 ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
            <p className="tw-text-sm tw-text-gray-400 tw-mb-0">Aucun message</p>
          </div>
        ) : (
          <>
            <MessageThread
              messages={messages}
              currentUserId=""
              onLoadMore={handleLoadMore}
              hasMore={hasMore}
              loadingMore={loadingMore}
            />
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input zone — only when not readOnly */}
      {!readOnly && !showInput && (
        <div className="tw-border-t tw-border-gray-200 tw-bg-white tw-flex-shrink-0 tw-rounded-b-xl tw-p-3">
          <button
            type="button"
            onClick={() => setShowInput(true)}
            className="tw-w-full tw-py-2 tw-px-4 tw-rounded-lg tw-bg-affinicar-primary tw-text-white tw-text-sm tw-font-medium hover:tw-bg-affinicar-secondary tw-transition-colors tw-border-none tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-gap-2"
          >
            <Send className="tw-w-4 tw-h-4" />
            Envoyer un message à Affinicar
          </button>
        </div>
      )}
      {!readOnly && showInput && (
        <div className="tw-border-t tw-border-gray-200 tw-bg-white tw-flex-shrink-0 tw-rounded-b-xl">
          {/* Pending files */}
          {pendingFiles.length > 0 && (
            <div className="tw-px-3 tw-pt-2 tw-flex tw-flex-wrap tw-gap-1.5">
              {pendingFiles.map((file, i) => (
                <div key={i} className="tw-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-rounded-lg tw-px-2 tw-py-1 tw-text-xs tw-text-gray-700">
                  <Paperclip className="tw-w-3 tw-h-3 tw-text-gray-400" />
                  <span className="tw-max-w-[80px] sm:tw-max-w-[120px] tw-truncate">{file.name}</span>
                  <button onClick={() => removePendingFile(i)} className="tw-text-gray-400 hover:tw-text-red-500 tw-ml-0.5 tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer">
                    <X className="tw-w-3 tw-h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* File error */}
          {fileError && (
            <div className="tw-px-3 tw-pt-1.5 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-red-600">
              <AlertCircle className="tw-w-3 tw-h-3 tw-flex-shrink-0" />
              {fileError}
            </div>
          )}
          {/* Input row */}
          <div className="tw-flex tw-items-end tw-gap-2 tw-px-3 tw-py-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              className="tw-hidden"
              onChange={handleFileSelect}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending}
              className="tw-flex-shrink-0 tw-w-9 tw-h-9 tw-rounded-lg tw-text-gray-400 tw-flex tw-items-center tw-justify-center hover:tw-text-affinicar-primary hover:tw-bg-gray-100 tw-transition-colors disabled:tw-opacity-40 tw-bg-transparent tw-border-none tw-cursor-pointer"
            >
              <Paperclip className="tw-w-4 tw-h-4" />
            </button>
            <textarea
              ref={inputRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Écrivez un message..."
              rows={1}
              className="tw-flex-1 tw-resize-none tw-border tw-border-gray-200 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-transparent tw-max-h-24"
              style={{ minHeight: '36px' }}
              disabled={isSending}
            />
            <button
              onClick={handleSend}
              disabled={(!inputText.trim() && pendingFiles.length === 0) || isSending}
              className="tw-flex-shrink-0 tw-w-9 tw-h-9 tw-rounded-lg tw-bg-affinicar-primary tw-text-white tw-flex tw-items-center tw-justify-center hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-border-none tw-cursor-pointer"
            >
              {isSending ? (
                <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
              ) : (
                <Send className="tw-w-4 tw-h-4" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
